import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React, { forwardRef } from 'react';
import { ThemeButtonKeys } from '../../styles/tokens/buttons';
import { fontWeight } from '../../styles/tokens/typography';
import { useButtonStyles } from '../../hooks';
import { ArrowLeft, ArrowRight } from '../icon';
import { LinkTo } from '../linkTo/LinkTo';

export type ButtonType = HTMLButtonElement | HTMLAnchorElement;

export interface ButtonProps
  extends Partial<Omit<HTMLButtonElement, 'children'>>,
    Partial<
      Omit<
        HTMLAnchorElement,
        'addEventListener' | 'removeEventListener' | 'children'
      >
    > {
  variant?: ThemeButtonKeys;
  label?: string;
  url?: string;
  as?: string;
  a11yTitle?: string;
  dataTest?: string;
  arrowRight?: boolean;
  arrowLeft?: boolean;
  iconRight?: React.ReactNode;
  iconLeft?: React.ReactNode;
  onClick?: React.MouseEventHandler<ButtonType>;
  small?: boolean;
  children?: React.ReactNode | string;
  isLoading?: boolean;
  state?: any;
}

type StyledButtonProps = Pick<
  ButtonProps,
  'variant' | 'disabled' | 'small' | 'isLoading'
> & {
  iconOnly?: boolean;
};

type ButtonTypes = React.ReactNode | React.PropsWithChildren<any> | string;

const rotateAni = keyframes`
  0% {
    transform: translatex(-50%) translatey(-50%) rotate(0deg);
  }
  100% {
    transform: translatex(-50%) translatey(-50%) rotate(360deg);
  }
  `;

// Fixes the React does not recognize the `XXX` prop on a DOM element.
const BLACKLISTED_PROPS = [
  'iconOnly',
  'small',
  'isAdded',
  'alignTop',
  'as',
  'alignTop',
  'rotateIcon',
  'isLoading',
  'dataTest',
  'a11yTitle',
];
export const StyledButton = styled('a', {
  shouldForwardProp: (propName) =>
    // If not in black list then return true
    !BLACKLISTED_PROPS.includes(propName),
})<StyledButtonProps>`
  ${({
    theme: { space, media, borderRadii, colors },
    variant,
    small,
    iconOnly,
    isLoading,
  }) => {
    // Size

    let padding = `${space.small} ${space.large}`;
    let paddingDesktop;

    if (iconOnly) {
      padding = space.small;
    }

    if (small && !iconOnly) {
      padding = space.xSmall;
      paddingDesktop = `${space.xSmall} ${space.medium}`;
    }

    if (small && iconOnly) {
      padding = space.xxSmall;
    }

    return css`
      display: inline-flex;
      justify-content: center;
      cursor: pointer;
      align-items: center;
      gap: ${space.xxSmall};
      padding: ${padding};
      border: solid 2px;
      text-decoration: none;
      font-weight: ${fontWeight.bold};
      ${variant && useButtonStyles(variant)};
      border-radius: ${borderRadii.medium};
      max-height: ${space.xxLarge};
      flex-shrink: 0;

      // Focused

      &:focus {
        outline: none;
        box-shadow: 0 0 0 4px ${colors.systemFocus};
      }

      // Disabled

      &:disabled {
        cursor: default;
        opacity: 0.2;
        pointer-events: none;
      }

      ${isLoading &&
      css`
        position: relative;
        transition: all 0.3s linear;
        color: transparent;
        &:before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translatex(-50%) translatey(-50%);
          display: inline-block;
          width: ${space.medium};
          height: ${space.medium};
          border: 2px solid;
          border-radius: 50%;
          border-top-color: rgba(255, 255, 255, 0.7);
          border-left-color: rgba(255, 255, 255, 0.1);
          border-right-color: rgba(255, 255, 255, 0.5);
          border-bottom-color: rgba(255, 255, 255, 0.3);
          animation: ${rotateAni} 0.6s linear infinite;
          display: block;
        }
      `}

      ${small &&
      css`
        max-height: ${space.xLarge};
        svg {
          width: ${space.medium};
          height: ${space.medium};
        }
      `}

      @media (min-width: ${media.medium}) {
        ${small &&
        css`
          padding: ${paddingDesktop};
        `}
      }
    `;
  }};
`;

export const Button = forwardRef<ButtonType, ButtonProps>(
  (
    {
      children,
      a11yTitle,
      onClick,
      disabled = false,
      variant = 'primary',
      url,
      as,
      label,
      iconLeft,
      iconRight,
      arrowRight,
      arrowLeft,
      small,
      isLoading,
      dataTest,
      target,
      state,
      ...rest
    },
    ref
  ) => {
    // Handle click

    const handleClick = onClick;

    // Icons

    const IconLeft = iconLeft;
    const IconRight = iconRight;

    // Component as

    let component: ButtonTypes;

    if (as) {
      component = as;
    } else if (!url) {
      component = 'button';
    } else {
      component = LinkTo;
    }

    const iconOnly: boolean =
      (!!arrowLeft || !!arrowRight || !!iconRight || !!iconLeft) &&
      !label &&
      !children;

    // const StyledButtonComponent = StyledButton.withComponent(component);
    return (
      <StyledButton
        ref={ref}
        to={url}
        as={component}
        onClick={handleClick}
        aria-label={a11yTitle}
        variant={variant}
        disabled={disabled}
        aria-disabled={disabled}
        target={target}
        iconOnly={iconOnly}
        small={small}
        isLoading={isLoading}
        state={state}
        {...(dataTest && { 'data-test': dataTest })}
        {...rest}
      >
        {arrowLeft && <ArrowLeft />}
        {iconLeft && !arrowLeft ? IconLeft : ''}
        {children || label}
        {iconRight && !arrowRight ? IconRight : ''}
        {arrowRight && <ArrowRight />}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
